import { useEffect, useRef, useState } from 'react';
import debounce from 'debounce-promise';
import _ from 'lodash';
import { getIncidents } from 'common/services/incidents.service';
import useToast from 'components/hooks/useToast';
import { metricNames } from '@linearb/linjs-common';
import { getAllContributorsTeamId } from 'common/utils/utils';
import { useSelector } from 'react-redux';
import { teamsSelector } from 'state/selectors/teams';
import { repositoriesByIdSelector } from 'state/selectors/repositories';
import { getServicesSelector } from 'state/selectors/services';
import moment from 'moment';
import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import { useLocation } from 'react-router';

const defaultState = { items: [], total: 0 };

export const PAST_1_YEAR = {
  after: moment().subtract(1, 'year').format(SEARCH_DATE_FORMAT),
  before: moment().format(SEARCH_DATE_FORMAT)
};

const STATUSES = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  CLOSED: 'closed'
};

const statusMap = {
  [STATUSES.OPEN]: 'created',
  [STATUSES.IN_PROGRESS]: 'in-progress',
  [STATUSES.CLOSED]: 'done'
};

export const getIncidentsPayload = (
  params,
  allContributorsTeamId,
  metricName
) => {
  const { filter = {}, limit, offset } = params || {};
  const status = filter?.status?.map(status => statusMap[status] || '') || [];
  const timeRange = filter?.in_progress_between || PAST_1_YEAR;
  const teamIds = filter?.team_ids || null;
  const isTeamAllContributorsTeam =
    _.get(teamIds, '[0]', '') === allContributorsTeamId;
  const metrics = [{ name: metricNames.PM_CFR }, { name: metricNames.PM_MTTR }];
  const requestedMetrics = metricName
    ? metrics.filter(m => m.name === metricName)
    : metrics;
  return {
    group_by: teamIds && !isTeamAllContributorsTeam ? 'team' : 'organization',
    time_ranges: [timeRange],
    roll_up: 'custom',
    order_by: {},
    requested_metrics: requestedMetrics,
    ...(teamIds && !isTeamAllContributorsTeam ? { team_ids: teamIds } : {}),
    limit,
    offset,
    status,
    ..._.pick(filter, ['service_ids', 'repository_ids'])
  };
};

const useIncidents = () => {
  const location = useLocation();
  const locationState = useRef(location?.state);
  const metricName = locationState.current?.metric_name;

  const [incidents, setIncidents] = useState(defaultState);
  const [enrichedEncidents, setEnrichedIncidents] = useState(defaultState);

  const teams = useSelector(teamsSelector) || [];
  const allContributorsTeamId = getAllContributorsTeamId(teams);
  const repositories = useSelector(repositoriesByIdSelector);

  const services = useSelector(getServicesSelector);
  const toast = useToast();

  useEffect(() => {
    if (incidents.items) {
      setEnrichedIncidents({
        ...incidents,
        items: resolveItemsReposServices(incidents.items)
      });
    }
  }, [services, repositories, incidents]);

  const resolveItemsReposServices = items => {
    try {
      return items?.map(incident => {
        if (incident.repository_ids?.length) {
          incident.repositories = incident.repository_ids.map(
            id => repositories[id]
          );
        }
        if (incident.service_ids?.length && services?.length) {
          incident.services = incident.service_ids.map(serviceId =>
            services.find(serviceItem => serviceItem.id === serviceId)
          );
        }
        return incident;
      });
    } catch (exp) {
      return items;
    }
  };

  const fetchIncidents = debounce(async params => {
    try {
      const incidentsRes = await getIncidents({
        ...getIncidentsPayload(params, allContributorsTeamId, metricName)
      });

      setIncidents(incidentsRes);
    } catch (e) {
      setIncidents(defaultState);
      toast({
        variant: 'error',
        message: 'Ooops... Something went wrong'
      });
    }
  }, 1000);

  return {
    incidents: enrichedEncidents,
    fetchIncidents
  };
};

export default useIncidents;
